import { lazy, type ComponentType } from "react";
import { ChunkUnavailable } from "../components/ui/chunkUnavailable.tsx";
import { notifyUpdateAvailable } from "../util/appUpdate.ts";

export interface ToolDefinition {
  id: string;
  name: string;
  component: ComponentType;
  checkAccess?: (canRead: (rowId: number) => boolean, pages: { RowID: number; Name: string }[]) => boolean;
  shortcutKey?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lz = (factory: () => Promise<Record<string, any>>, name: string) =>
  lazy(() =>
    factory()
      .then(m => ({ default: m[name] }))
      // Stale-after-deploy chunk → graceful placeholder + update prompt, never a throw.
      .catch(() => { notifyUpdateAvailable(true); return { default: ChunkUnavailable }; }));

const TaxCalculatorTool = lz(() => import("./TaxCalculatorTool.tsx"), "TaxCalculatorTool");
const CurrencyConverterTool = lz(() => import("./CurrencyConverterTool.tsx"), "CurrencyConverterTool");
const IsoCurrencyTool = lz(() => import("./IsoCurrencyTool.tsx"), "IsoCurrencyTool");
const ProductLookupTool = lz(() => import("./ProductLookupTool.tsx"), "ProductLookupTool");
const UserLookupTool = lz(() => import("./UserLookupTool.tsx"), "UserLookupTool");
const BlockCheckTool = lz(() => import("./BlockCheckTool.tsx"), "BlockCheckTool");
const ViewEmailTool = lz(() => import("./ViewEmailTool.tsx"), "ViewEmailTool");
const KioskLocationTool = lz(() => import("./KioskLocationTool.tsx"), "KioskLocationTool");
const MyFilesTool = lz(() => import("./MyFilesTool.tsx"), "MyFilesTool");

function hasPagePermission(pageName: string, canRead: (id: number) => boolean, pages: { RowID: number; Name: string }[]): boolean {
  const page = pages.find((p) => p.Name.toLowerCase() === pageName.toLowerCase());
  return page ? canRead(page.RowID) : false;
}

export const toolDefinitions: ToolDefinition[] = [
  {
    id: "kiosklocation",
    name: "Kiosk/Location Assist",
    component: KioskLocationTool,
    shortcutKey: "M",
  },
  {
    id: "taxcalculator",
    name: "Tax Calculator",
    component: TaxCalculatorTool,
    shortcutKey: "E",
  },
  {
    id: "currencyconverter",
    name: "Currency Converter",
    component: CurrencyConverterTool,
    shortcutKey: "4",
  },
  {
    id: "isocurrency",
    name: "ISO Currency",
    component: IsoCurrencyTool,
  },
  {
    id: "productlookup",
    name: "Product Lookup",
    component: ProductLookupTool,
    shortcutKey: "L",
  },
  {
    id: "userlookup",
    name: "User Lookup",
    component: UserLookupTool,
    shortcutKey: "U",
  },
  {
    id: "blockcheck",
    name: "User Block-Check",
    component: BlockCheckTool,
  },
  {
    id: "viewemail",
    name: "View Email",
    component: ViewEmailTool,
    checkAccess: (canRead, pages) => hasPagePermission("customerEmailLog", canRead, pages),
  },
  {
    id: "exportedreports",
    name: "Exported Reports",
    component: MyFilesTool,
    shortcutKey: "X",
  },
];
